/*
 * <tab-set> — accessible tabs. Put role="tab" buttons and role="tabpanel" containers
 * inside it; each tab's aria-controls points at its panel's id.
 */
class TabSet extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')]
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')]

    this.addEventListener('click', this.handleClick.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))

    // A link such as /pages/size-guide#vest opens the tab whose data-tab-id is "vest"
    this.handleHashChange = this.selectTabFromHash.bind(this)
    window.addEventListener('hashchange', this.handleHashChange)
    this.selectTabFromHash()
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.handleHashChange)
  }

  selectTabFromHash() {
    const tabId = window.location.hash.slice(1)
    const tab = this.tabs.find((candidate) => candidate.dataset.tabId === tabId)
    if (!tab) return

    this.selectTab(tab)
    this.scrollIntoView()
  }

  handleClick(event) {
    const tab = event.target.closest('[role="tab"]')
    if (tab) this.selectTab(tab)
  }

  handleKeydown(event) {
    const currentIndex = this.tabs.indexOf(document.activeElement)
    if (currentIndex === -1) return

    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
    if (!step) return

    event.preventDefault()
    const nextTab = this.tabs[(currentIndex + step + this.tabs.length) % this.tabs.length]
    this.selectTab(nextTab)
    nextTab.focus()
  }

  selectTab(selectedTab) {
    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab
      tab.setAttribute('aria-selected', isSelected)
      tab.tabIndex = isSelected ? 0 : -1
    })
    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== selectedTab.getAttribute('aria-controls')
    })
  }
}

if (!customElements.get('tab-set')) {
  customElements.define('tab-set', TabSet)
}
